import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../api";

/* The public end of a set-password link.
 *
 * Unlike every other screen in this app, whoever is here is NOT signed in and
 * did not navigate here — they followed a link an administrator sent them
 * through some channel IronForge does not control. Two consequences run
 * through the whole file:
 *
 * 1. The token is a live credential. It stays in the URL and in a variable,
 *    and never reaches the DOM: not a value, not a hidden input, not a data
 *    attribute. A screenshot of this page must not be a way in.
 *
 * 2. ⚠ A dead link has exactly one remedy, and it is not on this page.
 *    IronKeep shows the operator the link once and its redaction sweep wipes
 *    it from view after about five minutes, while the token itself lives 24
 *    hours — so the customer cannot be told to "use the link you were sent"
 *    and the operator cannot look it up. Asking their administrator to mint a
 *    new one is the only route back, and the copy says so rather than
 *    offering a retry that cannot work.
 */

// The same 12 the API enforces. Checked here as well as there so a typo costs
// a round trip rather than the customer's only link — the API refuses a short
// password before redeeming the token, but there is no reason to spend the
// trip at all.
const MIN_LENGTH = 12;

const ASK_YOUR_ADMIN =
  "This link is no longer valid. Ask your administrator to send you a new one.";

export function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError("Those two passwords do not match.");
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    try {
      await api.post("/api/auth/set-password", { token, password });
      // No session is issued here on purpose: setting a password revokes every
      // session for the account, so signing in is the next step and the one
      // that proves the new password works.
      navigate("/login", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 410) {
        // The server sends this same sentence. Repeating it here rather than
        // rendering whatever arrived keeps the one remedy on the screen even
        // if a proxy or a future change swallows the body.
        setError(ASK_YOUR_ADMIN);
      } else if (caught instanceof ApiError && caught.status === 422) {
        // FastAPI's validation detail is a list of field errors, which the API
        // client cannot render and a customer could not act on.
        setError(`That password was refused. Use at least ${MIN_LENGTH} characters.`);
      } else {
        setError(caught instanceof ApiError ? caught.message : "Something went wrong");
      }
      setBusy(false);
    }
  }

  return (
    <main className="shell">
      <form className="form stack" onSubmit={submit}>
        <div>
          <p className="wordmark" style={{ fontSize: "var(--step-1)" }}>
            IronForge<span> / control</span>
          </p>
          <h1 style={{ fontSize: "var(--step-2)" }}>Set your password</h1>
          <p className="lede">
            Choose a password for your IronForge account. You will sign in with it afterwards.
          </p>
        </div>

        {!token ? (
          <p className="notice notice--error" role="alert">
            This link is incomplete. Ask your administrator to send you a new one.
          </p>
        ) : (
          <>
            {error && (
              <p className="notice notice--error" role="alert">
                {error}
              </p>
            )}

            <label className="field">
              <span className="label">New password</span>
              <input
                id="set-password-new"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="label">Confirm password</span>
              <input
                id="set-password-confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </label>

            <p className="muted">At least {MIN_LENGTH} characters.</p>

            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? "Setting…" : "Set password"}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
